import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Image as ImageIcon, 
  Mic, 
  FileText, 
  Sparkles, 
  Download,
  Copy,
  Crown,
  CheckCircle,
  Upload,
  X,
  Wand2,
  Zap
} from 'lucide-react';
import { useGenovaAI } from '@/hooks/useGenovaAI';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ContentGenerator = () => {
  const { user } = useAuth();
  const { isGenerating, generateContent } = useGenovaAI();
  const [activeTab, setActiveTab] = useState('image');
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  
  // Listen for external tab changes from Dashboard
  React.useEffect(() => {
    const handleSetContentTab = (event: CustomEvent) => {
      const { tab } = event.detail;
      if (['image', 'voice', 'script'].includes(tab)) {
        setActiveTab(tab);
      }
    };
    
    window.addEventListener('setContentTab', handleSetContentTab as EventListener);
    return () => window.removeEventListener('setContentTab', handleSetContentTab as EventListener);
  }, []);
  
  // Form states
  const [imageForm, setImageForm] = useState({
    prompt: '',
    style: 'photographic',
    resolution: '512x512',
    realistic: false,
    uploadedImage: null as string | null
  });
  
  const [voiceForm, setVoiceForm] = useState({
    text: '',
    voice: 'Rachel'
  });
  
  const [scriptForm, setScriptForm] = useState({
    topic: '',
    scriptType: 'script'
  });

  const handleGenerate = async (type: string) => {
    if (!user) {
      toast.error('Please log in to use Genova AI Studio');
      return;
    }

    if (user.creditsUsed >= user.creditsLimit) {
      const planName = user.plan === 'free' ? 'Pro' : 'higher';
      toast.error(`You've reached your generation limit (${user.creditsLimit}). Upgrade to ${planName} plan for more generations!`);
      return;
    }

    let options;
    
    switch (type) {
      case 'image':
        if (!imageForm.prompt.trim() && !imageForm.uploadedImage) {
          toast.error('Please enter a prompt or upload an image');
          return;
        }
        const maxResolution = user.plan === 'team' ? '2048x2048' : user.plan === 'pro' ? '1536x1536' : '1024x1024';
        options = { 
          type: 'image', 
          ...imageForm,
          resolution: imageForm.resolution === 'auto' ? maxResolution : imageForm.resolution,
          realistic: imageForm.realistic,
          uploadedImage: imageForm.uploadedImage
        };
        break;
        
      case 'voice':
        if (!voiceForm.text.trim()) {
          toast.error('Please enter text for voiceover');
          return;
        }
        options = { type: 'voice', prompt: voiceForm.text, text: voiceForm.text, ...voiceForm };
        break;
        
      case 'script':
        if (!scriptForm.topic.trim()) {
          toast.error('Please enter a topic');
          return;
        }
        options = { type: 'script', prompt: scriptForm.topic, ...scriptForm };
        break;
        
      default:
        return;
    }

    const result = await generateContent(options);
    if (result) {
      setGeneratedContent({ type, ...result });
      
      if (type === 'script' && result.script) {
        toast.success('Script generated successfully!');
      }
    }
  };

  const getResolutionOptions = () => {
    const userPlan = user?.plan || 'free';
    switch (userPlan) {
      case 'free':
        return [
          { value: '512x512', label: '512px (Free limit)' }
        ];
      case 'starter':
        return [
          { value: '512x512', label: '512px' },
          { value: '1024x1024', label: '1024px HD' }
        ];
      case 'pro':
        return [
          { value: '512x512', label: '512px' },
          { value: '1024x1024', label: '1024px HD' },
          { value: '1536x1536', label: '1536px Full HD' }
        ];
      case 'team':
        return [
          { value: '512x512', label: '512px' },
          { value: '1024x1024', label: '1024px HD' },
          { value: '1536x1536', label: '1536px Full HD' },
          { value: '2048x2048', label: '2048px 4K' }
        ];
      default:
        return [{ value: '512x512', label: '512px (Free limit)' }];
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getCreditsRemaining = () => {
    if (!user) return 0;
    return Math.max(0, user.creditsLimit - user.creditsUsed);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 via-background to-muted/50 p-3 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Modern Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-14 bg-card border border-border p-1 rounded-2xl shadow-lg">
            <TabsTrigger 
              value="image" 
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-500 data-[state=active]:text-white rounded-xl transition-all duration-300"
            >
              <ImageIcon className="w-4 h-4" />
              <span className="hidden sm:inline">AI Images</span>
              <span className="sm:hidden">Images</span>
              <CheckCircle className="w-4 h-4 text-green-500 data-[state=active]:text-white" />
            </TabsTrigger>
            <TabsTrigger 
              value="voice"
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-teal-500 data-[state=active]:text-white rounded-xl transition-all duration-300"
            >
              <Mic className="w-4 h-4" />
              <span className="hidden sm:inline">AI Voice</span>
              <span className="sm:hidden">Voice</span>
              <CheckCircle className="w-4 h-4 text-green-500 data-[state=active]:text-white" />
            </TabsTrigger>
            <TabsTrigger 
              value="script" 
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-pink-500 data-[state=active]:text-white rounded-xl transition-all duration-300"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">AI Scripts</span>
              <span className="sm:hidden">Scripts</span>
              <CheckCircle className="w-4 h-4 text-green-500 data-[state=active]:text-white" />
            </TabsTrigger>
          </TabsList>

          {/* Image Generator */}
          <TabsContent value="image" className="mt-6 space-y-6">
            <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden bg-card">
              {/* Header */}
              <CardHeader className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-6 sm:p-8">
                <CardTitle className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <ImageIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="text-xl sm:text-2xl font-bold">Professional AI Image Generator</h3>
                    <p className="text-white/80 text-sm font-normal mt-1">
                      Powered by Gemini (Nano Banana) • High-quality image generation & edits
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6 sm:p-8 space-y-6">
                {/* Credits Notice */}
                <div className="flex items-center justify-center p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-2xl">
                  <span className="text-lg mr-2">🎉</span>
                  <p className="text-blue-700 dark:text-blue-300 text-sm sm:text-base font-medium">
                    Free users can now generate images! You have <span className="font-bold">{getCreditsRemaining()}</span> credits remaining this month.
                  </p>
                </div>

                {/* Upload Image Section */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Upload Image to Customize (Optional)
                  </Label>
                  
                  {!imageForm.uploadedImage ? (
                    <div className="relative">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          // Allow re-selecting the same file later
                          e.currentTarget.value = '';

                          if (!file) return;
                          if (!file.type.startsWith('image/')) {
                            toast.error('Please upload a valid image file');
                            return;
                          }
                          if (file.size > 6 * 1024 * 1024) {
                            toast.error('Image too large (max 6MB)');
                            return;
                          }

                          const reader = new FileReader();
                          reader.onerror = () => toast.error('Failed to read the uploaded image');
                          reader.onloadend = () => {
                            const result = reader.result;
                            if (typeof result !== 'string') {
                              toast.error('Failed to process the uploaded image');
                              return;
                            }
                            setImageForm((prev) => ({ ...prev, uploadedImage: result }));
                          };
                          reader.readAsDataURL(file);
                        }}
                        className="h-14 border-2 border-dashed border-border hover:border-primary rounded-2xl cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 transition-colors"
                      />
                    </div>
                  ) : (
                    <div className="relative inline-block">
                      <img 
                        src={imageForm.uploadedImage} 
                        alt="Uploaded" 
                        className="w-32 h-32 object-cover rounded-2xl border-2 border-border shadow-lg"
                      />
                      <Button
                        size="icon"
                        variant="destructive"
                        onClick={() => setImageForm({ ...imageForm, uploadedImage: null })}
                        className="absolute -top-2 -right-2 w-8 h-8 rounded-full shadow-lg"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Prompt Input */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Wand2 className="w-4 h-4" />
                    {imageForm.uploadedImage ? 'Describe How to Customize' : 'Describe Your Vision'}
                  </Label>
                  <Textarea
                    placeholder={imageForm.uploadedImage 
                      ? "Add vintage filter, make it brighter, change background to sunset..." 
                      : "A majestic lion in golden sunlight, cinematic photography, ultra detailed..."
                    }
                    value={imageForm.prompt}
                    onChange={(e) => setImageForm({ ...imageForm, prompt: e.target.value })}
                    rows={4}
                    className="text-base border-2 border-border focus:border-primary rounded-2xl resize-none transition-colors"
                  />
                </div>
                
                {/* Options Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Art Style</Label>
                    <Select 
                      value={imageForm.style} 
                      onValueChange={(value) => setImageForm({ ...imageForm, style: value })}
                    >
                      <SelectTrigger className="h-12 border-2 border-border focus:border-primary rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="photographic">📸 Photographic</SelectItem>
                        <SelectItem value="digital-art">🎨 Digital Art</SelectItem>
                        <SelectItem value="fantasy-art">🧙‍♂️ Fantasy</SelectItem>
                        <SelectItem value="anime">🎌 Anime</SelectItem>
                        <SelectItem value="3d-model">🎯 3D Model</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Resolution</Label>
                    <Select 
                      value={imageForm.resolution} 
                      onValueChange={(value) => setImageForm({ ...imageForm, resolution: value })}
                    >
                      <SelectTrigger className="h-12 border-2 border-border focus:border-primary rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getResolutionOptions().map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Realistic Mode Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl border border-border">
                  <div className="space-y-1">
                    <Label htmlFor="realistic-mode" className="text-sm font-semibold flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      Realistic Mode
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Ultra-realistic images using Google Imagen AI
                    </p>
                  </div>
                  <Switch
                    id="realistic-mode"
                    checked={imageForm.realistic}
                    onCheckedChange={(checked) => setImageForm({ ...imageForm, realistic: checked })}
                  />
                </div>
                
                {/* Generate Button */}
                <Button 
                  onClick={() => handleGenerate('image')} 
                  disabled={isGenerating || (!imageForm.prompt.trim() && !imageForm.uploadedImage) || getCreditsRemaining() <= 0}
                  className="w-full h-14 text-base font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  {isGenerating ? (
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 animate-spin" />
                      <span>Creating Magic...</span>
                    </div>
                  ) : getCreditsRemaining() <= 0 ? (
                    <div className="flex items-center gap-2">
                      <Crown className="w-5 h-5" />
                      <span>No Credits Remaining</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      <span>Generate Image ({getCreditsRemaining()} credits left)</span>
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Voice Generator */}
          <TabsContent value="voice" className="mt-6 space-y-6">
            <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden bg-card">
              <CardHeader className="bg-gradient-to-r from-green-500 via-teal-500 to-cyan-500 p-6 sm:p-8">
                <CardTitle className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <Mic className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="text-xl sm:text-2xl font-bold">Professional AI Voice Generator</h3>
                    <p className="text-white/80 text-sm font-normal mt-1">
                      Powered by ElevenLabs • Ultra-realistic voices
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6 sm:p-8 space-y-6">
                <div className="flex items-center justify-center p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-2xl">
                  <span className="text-lg mr-2">🎉</span>
                  <p className="text-green-700 dark:text-green-300 text-sm sm:text-base font-medium">
                    You have <span className="font-bold">{getCreditsRemaining()}</span> credits remaining this month.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Your Script</Label>
                  <Textarea
                    placeholder="Enter the text you want to convert to professional voiceover..."
                    value={voiceForm.text}
                    onChange={(e) => setVoiceForm({ ...voiceForm, text: e.target.value })}
                    rows={5}
                    className="text-base border-2 border-border focus:border-green-500 rounded-2xl resize-none"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Voice Character</Label>
                  <Select 
                    value={voiceForm.voice} 
                    onValueChange={(value) => setVoiceForm({ ...voiceForm, voice: value })}
                  >
                    <SelectTrigger className="h-12 border-2 border-border focus:border-green-500 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Rachel">👩 Rachel (Professional Female)</SelectItem>
                      <SelectItem value="Drew">👨 Drew (Confident Male)</SelectItem>
                      <SelectItem value="Clyde">🎭 Clyde (Warm Male)</SelectItem>
                      <SelectItem value="Dave">🎪 Dave (Energetic Male)</SelectItem>
                      <SelectItem value="Aria">🌟 Aria (Premium)</SelectItem>
                      <SelectItem value="Roger">🎙️ Roger (Premium)</SelectItem>
                      <SelectItem value="Sarah">🎤 Sarah (Premium)</SelectItem>
                      <SelectItem value="Laura">🎼 Laura (Premium)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={() => handleGenerate('voice')} 
                  disabled={isGenerating || !voiceForm.text.trim() || getCreditsRemaining() <= 0}
                  className="w-full h-14 text-base font-bold bg-gradient-to-r from-green-600 via-teal-600 to-cyan-600 hover:from-green-700 hover:via-teal-700 hover:to-cyan-700 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  {isGenerating ? (
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 animate-spin" />
                      <span>Generating Voice...</span>
                    </div>
                  ) : getCreditsRemaining() <= 0 ? (
                    <div className="flex items-center gap-2">
                      <Crown className="w-5 h-5" />
                      <span>No Credits Remaining</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Mic className="w-5 h-5" />
                      <span>Generate Voice ({getCreditsRemaining()} credits left)</span>
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Script Generator */}
          <TabsContent value="script" className="mt-6 space-y-6">
            <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden bg-card">
              <CardHeader className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 p-6 sm:p-8">
                <CardTitle className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="text-xl sm:text-2xl font-bold">AI Script Generator</h3>
                    <p className="text-white/80 text-sm font-normal mt-1">
                      Powered by OpenAI • Viral content scripts
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6 sm:p-8 space-y-6">
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Content Topic</Label>
                  <Input
                    placeholder="Enter your video topic, URL, or content idea..."
                    value={scriptForm.topic}
                    onChange={(e) => setScriptForm({ ...scriptForm, topic: e.target.value })}
                    className="h-14 text-base border-2 border-border focus:border-orange-500 rounded-2xl"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Script Type</Label>
                  <Select value={scriptForm.scriptType} onValueChange={(value) => setScriptForm({ ...scriptForm, scriptType: value })}>
                    <SelectTrigger className="h-12 border-2 border-border focus:border-orange-500 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="script">📝 Full Video Script</SelectItem>
                      <SelectItem value="youtube_title">🎬 YouTube Titles</SelectItem>
                      <SelectItem value="hooks">🎣 Hooks & Openers</SelectItem>
                      <SelectItem value="hashtags">📱 Strategic Hashtags</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={() => handleGenerate('script')} 
                  disabled={isGenerating || !scriptForm.topic.trim() || getCreditsRemaining() <= 0}
                  className="w-full h-14 text-base font-bold bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 hover:from-orange-700 hover:via-red-700 hover:to-pink-700 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  {isGenerating ? (
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 animate-spin" />
                      <span>Creating Script...</span>
                    </div>
                  ) : getCreditsRemaining() <= 0 ? (
                    <div className="flex items-center gap-2">
                      <Crown className="w-5 h-5" />
                      <span>No Credits Remaining</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      <span>Generate Script ({getCreditsRemaining()} credits left)</span>
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Generated Content Display */}
        {generatedContent && (
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden bg-card animate-in fade-in-0 zoom-in-95 duration-500">
            <CardHeader className="bg-gradient-to-r from-gray-800 to-gray-900 p-6">
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Generated Content
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 sm:p-8">
              {generatedContent.type === 'image' && (
                <div className="space-y-6">
                  <div className="relative rounded-2xl overflow-hidden">
                    <img 
                      src={generatedContent.imageUrl} 
                      alt="Generated image" 
                      className="w-full max-w-3xl mx-auto rounded-2xl shadow-2xl"
                    />
                    {generatedContent.watermarked && (
                      <div className="absolute bottom-4 right-4 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-xl shadow-lg">
                        <div className="flex items-center gap-2">
                          <Crown className="w-4 h-4" />
                          <span className="text-sm font-semibold">Free User</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Button 
                      variant="outline" 
                      onClick={async () => {
                        try {
                          const userPlan = user?.plan || 'free';
                          const resolution = generatedContent.resolution || '512x512';
                          const [width] = resolution.split('x').map(Number);

                          if (userPlan === 'free' && width > 512) {
                            toast.error('Free users can only download 512x512 images. Upgrade to download higher resolutions!');
                            return;
                          }

                          const fileName = `genova-ai-image-${Date.now()}.png`;
                          const downloadDataUrl = generatedContent.downloadDataUrl;

                          // Permanent fix: always prefer server-returned base64 (no CORS issues)
                          if (typeof downloadDataUrl === 'string' && downloadDataUrl.startsWith('data:image/')) {
                            let urlToDownload = downloadDataUrl;

                            if (userPlan === 'free') {
                              const { applyWatermarkToImage } = await import('@/utils/watermark');
                              urlToDownload = await applyWatermarkToImage(downloadDataUrl, 'GENOVA AI STUDIO');
                            } else {
                              // Convert dataURL -> blob URL for more reliable downloads across browsers
                              const response = await fetch(downloadDataUrl);
                              const blob = await response.blob();
                              urlToDownload = URL.createObjectURL(blob);
                            }

                            const link = document.createElement('a');
                            link.href = urlToDownload;
                            link.download = fileName;
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();

                            setTimeout(() => {
                              document.body.removeChild(link);
                              if (urlToDownload.startsWith('blob:')) URL.revokeObjectURL(urlToDownload);
                            }, 250);

                            toast.success('Image downloaded successfully!');
                            return;
                          }

                          // Fallback if no base64 provided (should be rare)
                          const fallbackUrl = generatedContent.imageUrl;
                          const link = document.createElement('a');
                          link.href = fallbackUrl;
                          link.download = fileName;
                          link.target = '_blank';
                          link.rel = 'noopener noreferrer';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);

                          toast.info('Opened the image in a new tab. Use Save As to download.');
                        } catch (error) {
                          console.error('Download error:', error);
                          toast.error('Failed to download image. Please try again.');
                        }
                      }}
                      className="px-6 py-3 font-semibold rounded-xl border-2"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Image
                    </Button>
                    {generatedContent.watermarked && user?.plan === 'free' && (
                      <Button 
                        className="px-6 py-3 font-semibold rounded-xl bg-gradient-to-r from-purple-600 to-pink-600"
                        onClick={() => window.location.href = '/pricing'}
                      >
                        <Crown className="w-5 h-5 mr-2" />
                        Remove Watermark
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {generatedContent.type === 'voice' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-950/20 dark:to-teal-950/20 p-6 rounded-2xl">
                    <audio 
                      src={generatedContent.audioUrl} 
                      controls 
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-center">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = generatedContent.audioUrl;
                        link.download = 'genova-ai-voice.mp3';
                        link.click();
                      }}
                      className="px-6 py-3 font-semibold rounded-xl border-2"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Audio
                    </Button>
                  </div>
                </div>
              )}
              
              {generatedContent.type === 'script' && generatedContent.script && (
                <div className="space-y-6">
                  <div className="bg-muted/50 p-6 rounded-2xl">
                    <pre className="whitespace-pre-wrap text-sm leading-relaxed font-mono">
                      {generatedContent.script}
                    </pre>
                  </div>
                  <div className="flex justify-center">
                    <Button 
                      variant="outline" 
                      onClick={() => copyToClipboard(generatedContent.script)}
                      className="px-6 py-3 font-semibold rounded-xl border-2"
                    >
                      <Copy className="w-5 h-5 mr-2" />
                      Copy Script
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ContentGenerator;
